/**
 * QuestionCounterManager
 * Manages user question counter in Firebase Firestore
 *
 * Changes vs your version:
 * - Transactional ensureCountField() to prevent concurrent "add missing Count"
 * - In-flight de-duplication (_withLock) so multiple callers coalesce
 * - initialize() listener uses `{ once: true }` to avoid double init
 * - Safer nullish checks (??) so Count=0 is preserved
 * - Minor logging and cache consistency polish
 */
export class QuestionCounterManager {
    constructor() {
        this.db = null;
        this.modules = null;
        this.isReady = false;

        this.pendingDecrements = 0;
        this.cachedCount = null; // cache last known Count
        
        // Track subscription status. Backed by `_isSubscribed` (last value we
        // were told via the `subscriptionStatusChecked` event) but READ through
        // a getter that ALSO consults the two live subscription checkers
        // (window.subscriptionChecker + window.backendSubscriptionChecker).
        // This makes send-time the authoritative source of truth instead of
        // relying solely on having caught the event in time: if EITHER checker
        // currently reports subscribed, the user is treated as subscribed, so a
        // late/missed event or a "not subscribed" report from one checker can't
        // wrongly block a genuine subscriber.
        this._isSubscribed = false;

        // simple lock map to deduplicate concurrent calls
        this._locks = new Map();

        this.initPromise = this.initialize();
        
        // Listen for subscription status updates
        this.listenForSubscriptionUpdates();
    }

    // -----------------------------
    // Subscription state (authoritative at read time)
    // -----------------------------
    /**
     * AUTHORITATIVE subscription check. Returns true if EITHER live checker
     * currently reports the user as subscribed, OR we previously caught a
     * "subscribed" event. Reading the live checkers here (rather than trusting
     * only the cached event value) closes the race where the event fired before
     * this manager started listening, arrived late, or where one checker said
     * "not subscribed" and clobbered a confirmed "subscribed" from the other.
     */
    get isSubscribed() {
        if (this._isSubscribed === true) return true;
        try {
            if (window.subscriptionChecker?.isSubscribed === true) return true;
            if (window.backendSubscriptionChecker?.isSubscribed === true)
                return true;
            if (window.userIsSubscribed === true) return true;
        } catch (_e) {
            /* window access guard — fall through to false */
        }
        return false;
    }

    set isSubscribed(value) {
        // Store the last value we were explicitly told (e.g. from the event or
        // on sign-out). The getter still OR's this with the live checkers so a
        // stale `false` here can't override a checker that says subscribed.
        this._isSubscribed = value === true;
    }

    /**
     * When a logged-in user appears to be out of quota, make a best effort to
     * resolve their subscription status BEFORE blocking them. This covers the
     * brief window after page load where neither checker has finished its async
     * backend/Firestore lookup yet. Triggers a fresh backend check (if needed)
     * and awaits whatever subscription resolution is in flight. Returns the
     * authoritative subscribed flag afterwards.
     */
    async confirmSubscriptionBeforeBlock() {
        if (this.isSubscribed) return true;

        const waits = [];
        try {
            const backend = window.backendSubscriptionChecker;
            if (backend) {
                // Kick a fresh backend lookup so a subscriber whose status
                // hasn't loaded yet isn't blocked during that window.
                if (typeof backend.checkSubscriptionStatus === "function") {
                    waits.push(
                        Promise.resolve(
                            backend.checkSubscriptionStatus(),
                        ).catch(() => {}),
                    );
                } else if (backend.initPromise) {
                    waits.push(Promise.resolve(backend.initPromise).catch(() => {}));
                }
            }
            const front = window.subscriptionChecker;
            if (front) {
                if (typeof front.refreshStatus === "function") {
                    waits.push(
                        Promise.resolve(front.refreshStatus()).catch(() => {}),
                    );
                } else if (front.initPromise) {
                    waits.push(Promise.resolve(front.initPromise).catch(() => {}));
                }
            }
        } catch (_e) {
            /* defensive: ignore and fall through */
        }

        if (waits.length) {
            // Don't hang the send forever if a checker stalls.
            const timeout = new Promise((resolve) =>
                setTimeout(resolve, 4000),
            );
            try {
                await Promise.race([Promise.all(waits), timeout]);
            } catch (_e) {
                /* ignore */
            }
        }

        return this.isSubscribed;
    }

    // -----------------------------
    // Init / readiness
    // -----------------------------
    async initialize() {
        if (this.isReady) return;

        return new Promise((resolve, reject) => {
            // If Firebase already injected on window
            if (window.firebaseDB && window.firebaseModules) {
                this.db = window.firebaseDB;
                this.modules = window.firebaseModules;
                this.isReady = true;
                console.log("[QuestionCounter] Firebase already initialized");
                resolve();
                return;
            }

            // Single-fire listener prevents duplicate init
            const onFirebaseReady = (event) => {
                this.db = event.detail.db;
                this.modules = event.detail.modules;
                this.isReady = true;
                console.log(
                    "[QuestionCounter] Firebase initialized from event",
                );
                resolve();
            };

            // Add once to avoid multiple handlers being registered
            window.addEventListener("firebaseReady", onFirebaseReady, {
                once: true,
            });

            // Timeout after 10 seconds
            setTimeout(() => {
                if (!this.isReady) {
                    console.error(
                        "[QuestionCounter] Firebase initialization timeout",
                    );
                    reject(new Error("Firebase initialization timeout"));
                }
            }, 10000);
        });
    }

    async ensureReady() {
        if (!this.isReady) {
            await this.initPromise;
        }
        if (!this.db || !this.modules) {
            throw new Error("Firebase not initialized");
        }
    }

    // -----------------------------
    // Small in-flight lock utility
    // -----------------------------
    async _withLock(key, fn) {
        if (this._locks.has(key)) return this._locks.get(key);
        const p = (async () => {
            try {
                return await fn();
            } finally {
                this._locks.delete(key);
            }
        })();
        this._locks.set(key, p);
        return p;
    }

    // -----------------------------
    // Transactional guard to ensure "Count" exists
    // -----------------------------
    async ensureCountField(userId) {
        await this.ensureReady();

        if (!userId) throw new Error("User ID is required");

        const { doc, runTransaction, serverTimestamp } = this.modules;
        const ref = doc(this.db, "userProfiles", userId);

        // De-duplicate concurrent callers at the app level
        return this._withLock(`ensureCountField:${userId}`, async () => {
            return runTransaction(this.db, async (tx) => {
                const snap = await tx.get(ref);

                if (!snap.exists()) {
                    const now = serverTimestamp?.() ?? new Date().toISOString();
                    tx.set(ref, {
                        Count: 10,
                        createdAt: now,
                        lastWriter: "QCM:ensureCountField.create",
                        lastWriteAt: now,
                    });
                    console.log(
                        `[QuestionCounter] Created user with Count 10 (transaction)`,
                    );
                    return { created: true, migrated: false, count: 10 };
                }

                const data = snap.data() || {};
                const hasCount = Object.prototype.hasOwnProperty.call(
                    data,
                    "Count",
                );

                if (hasCount) {
                    // nothing to do
                    return {
                        created: false,
                        migrated: false,
                        count: data.Count ?? 0,
                    };
                }

                // migrate from questionCount if present; otherwise default to 10
                const countValue =
                    typeof data.questionCount === "number"
                        ? data.questionCount
                        : 10;
                const now = serverTimestamp?.() ?? new Date().toISOString();

                tx.set(
                    ref,
                    {
                        Count: countValue,
                        lastWriter: "QCM:ensureCountField.migration",
                        lastWriteAt: now,
                    },
                    { merge: true },
                );

                console.log(
                    `[QuestionCounter] Migrated missing Count -> ${countValue} (transaction)`,
                );
                return { created: false, migrated: true, count: countValue };
            });
        });
    }

    // -----------------------------
    // Core document helpers
    // -----------------------------
    /**
     * Get the user document after guaranteeing Count exists.
     * De-duplicated so multiple simultaneous calls return the same promise.
     */
    async getUserDocument(userId) {
        if (!userId) throw new Error("User ID is required");

        return this._withLock(`getUserDocument:${userId}`, async () => {
            await this.ensureReady();
            await this.ensureCountField(userId); // transactional guard first

            const { doc, getDoc } = this.modules;
            const ref = doc(this.db, "userProfiles", userId);
            const snap = await getDoc(ref);

            if (!snap.exists()) {
                // very unlikely because ensureCountField creates it
                console.warn(
                    "[QuestionCounter] Document unexpectedly missing after ensureCountField",
                );
                return {};
            }

            const data = snap.data() || {};
            console.log("[QuestionCounter] User document found:", data);
            return data;
        });
    }

    async getQuestionCount(userId) {
        // If user is subscribed, return unlimited (represented as a large number)
        if (this.isSubscribed) {
            console.log("[QuestionCounter] User is subscribed - returning unlimited count");
            return 999999;
        }
        
        try {
            const userData = await this.getUserDocument(userId);
            const count = userData?.Count ?? 0; // preserve 0
            this.cachedCount = count;
            console.log(
                `[QuestionCounter] Current Count for user ${userId}: ${count}`,
            );
            return count;
        } catch (error) {
            console.error(
                "[QuestionCounter] Error getting question count:",
                error,
            );
            return 0; // safe fallback
        }
    }

    /**
     * Non-mutating quota peek for UI gating (e.g. the "interesting fact"
     * shortcut). Returns true when the user can still send: subscribed, or the
     * locally cached count (minus pending decrements) is still positive. When
     * the count hasn't loaded yet (cachedCount === null) it returns true so the
     * caller defers to the authoritative send path rather than blocking early.
     * Unlike fastLocalCheck()/reserveQuestion() this does NOT decrement or
     * reserve anything.
     */
    hasQuotaRemaining() {
        if (this.isSubscribed) return true;
        if (this.cachedCount === null) return true; // unknown — let send path decide
        return this.cachedCount - this.pendingDecrements > 0;
    }

    async getAvailableCount(userId) {
        const currentCount = await this.getQuestionCount(userId);
        const availableCount = currentCount - this.pendingDecrements;
        console.log(
            `[QuestionCounter] Available count: ${availableCount} (current: ${currentCount}, pending: ${this.pendingDecrements})`,
        );
        return availableCount;
    }

    /**
     * Synchronous reservation: do this immediately before long async work.
     * Returns true if we *think* we have quota locally.
     */
    reserveQuestion() {
        // Subscribed users always have quota
        if (this.isSubscribed) {
            console.log("[QuestionCounter] User is subscribed - reservation always succeeds");
            return true;
        }
        
        if (this.cachedCount === null) {
            console.warn("[QuestionCounter] No cached count available");
            return false;
        }
        const available = this.cachedCount - this.pendingDecrements;
        if (available > 0) {
            this.pendingDecrements++;
            console.log(
                `[QuestionCounter] Reserved question (pending: ${this.pendingDecrements}, will show ${available - 1} available)`,
            );
            return true;
        }
        console.log(
            "[QuestionCounter] Reservation failed - no questions remaining",
        );
        return false;
    }

    /**
     * Fast local-only check and decrement for instant message sending.
     * This immediately decrements the cached count without waiting for Firebase.
     * Returns true if we have quota locally, false otherwise.
     */
    fastLocalCheck() {
        // Subscribed users always pass fast check
        if (this.isSubscribed) {
            console.log("[QuestionCounter] User is subscribed - fast check always passes");
            return true;
        }
        
        if (this.cachedCount === null) {
            console.warn("[QuestionCounter] No cached count available for fast check");
            return false;
        }
        const available = this.cachedCount - this.pendingDecrements;
        if (available > 0) {
            // Immediately decrement local cache
            this.cachedCount--;
            console.log(
                `[QuestionCounter] Fast local check passed. New cached count: ${this.cachedCount}`,
            );
            return true;
        }
        console.log(
            "[QuestionCounter] Fast local check failed - no questions remaining",
        );
        return false;
    }

    /**
     * Asynchronously sync the decremented count to Firebase.
     * This is called after the message has already been sent.
     * Handles errors gracefully without blocking the user experience.
     */
    async syncDecrementToFirebase(userId) {
        // Skip Firebase sync for subscribed users
        if (this.isSubscribed) {
            console.log("[QuestionCounter] User is subscribed - skipping Firebase decrement sync");
            return 999999; // Return unlimited count
        }
        
        try {
            await this.ensureReady();
            if (!userId) throw new Error("User ID is required");

            const { doc, runTransaction, serverTimestamp } = this.modules;
            const ref = doc(this.db, "userProfiles", userId);

            const newCount = await runTransaction(this.db, async (tx) => {
                const snap = await tx.get(ref);

                if (!snap.exists()) {
                    // First-time write: start at 9 (10 - 1)
                    const now = serverTimestamp?.() ?? new Date().toISOString();
                    tx.set(ref, {
                        Count: 9,
                        createdAt: now,
                        lastQuestionAt: now,
                    });
                    console.log(
                        "[QuestionCounter] Created new user document with Count: 9 (async sync)",
                    );
                    return 9;
                }

                const data = snap.data() || {};
                const currentCount = data.Count ?? 0;

                if (currentCount <= 0) {
                    console.warn(
                        "[QuestionCounter] Firebase count already at 0 during async sync",
                    );
                    // Don't throw here - message was already sent
                    return 0;
                }

                const newVal = currentCount - 1;
                tx.update(ref, {
                    Count: newVal,
                    lastQuestionAt:
                        serverTimestamp?.() ?? new Date().toISOString(),
                });

                console.log(
                    `[QuestionCounter] Async Firebase sync: decremented from ${currentCount} to ${newVal}`,
                );
                return newVal;
            });

            // Update cache with Firebase value if different
            if (newCount !== this.cachedCount && newCount !== null) {
                console.log(
                    `[QuestionCounter] Syncing cache with Firebase value: ${newCount}`,
                );
                this.cachedCount = newCount;
            }

            return newCount;
        } catch (error) {
            console.error("[QuestionCounter] Async Firebase sync failed:", error);
            // Don't throw - this is background sync, don't interrupt user
            // The local count was already decremented
            return this.cachedCount;
        }
    }

    /**
     * Atomic decrement using Firestore transaction.
     * Does NOT increment pendingDecrements (reserveQuestion already did).
     */
    async decrementQuestionCount(userId) {
        // Skip decrement for subscribed users
        if (this.isSubscribed) {
            console.log("[QuestionCounter] User is subscribed - skipping Firebase decrement");
            return 999999; // Return unlimited count
        }
        
        await this.ensureReady();
        if (!userId) throw new Error("User ID is required");

        const { doc, runTransaction, serverTimestamp } = this.modules;
        const ref = doc(this.db, "userProfiles", userId);

        try {
            const newCount = await runTransaction(this.db, async (tx) => {
                const snap = await tx.get(ref);

                if (!snap.exists()) {
                    // First-time write: start at 9 (10 - 1)
                    const now = serverTimestamp?.() ?? new Date().toISOString();
                    tx.set(ref, {
                        Count: 9,
                        createdAt: now,
                        lastQuestionAt: now,
                    });
                    console.log(
                        "[QuestionCounter] Created new user document with Count: 9",
                    );
                    return 9;
                }

                const data = snap.data() || {};
                const currentCount = data.Count ?? 0;

                if (currentCount <= 0) {
                    // update cache and abort
                    this.cachedCount = 0;
                    console.log(
                        "[QuestionCounter] Count already at 0 - throwing error",
                    );
                    throw new Error("No questions remaining");
                }

                const newVal = currentCount - 1;
                tx.update(ref, {
                    Count: newVal,
                    lastQuestionAt:
                        serverTimestamp?.() ?? new Date().toISOString(),
                });

                console.log(
                    `[QuestionCounter] Decremented Count from ${currentCount} to ${newVal}`,
                );
                return newVal;
            });

            // reflect success in cache
            this.cachedCount = newCount;
            return newCount;
        } catch (error) {
            console.error("[QuestionCounter] Transaction failed:", error);
            throw error;
        } finally {
            // always release one pending reservation
            if (this.pendingDecrements > 0) this.pendingDecrements--;
        }
    }

    async canAskQuestion(userId) {
        // Subscribed users can always ask questions
        if (this.isSubscribed) {
            console.log(`[QuestionCounter] User ${userId} is subscribed - can always ask questions`);
            return true;
        }
        
        try {
            const count = await this.getQuestionCount(userId);
            const canAsk = count > 0;
            console.log(
                `[QuestionCounter] User ${userId} can ask question: ${canAsk} (count: ${count})`,
            );
            return canAsk;
        } catch (error) {
            console.error(
                "[QuestionCounter] Error checking if user can ask question:",
                error,
            );
            return false;
        }
    }

    listenForSubscriptionUpdates() {
        // Seed from any checker that already resolved BEFORE we started
        // listening — the event is fire-and-forget with no replay, so without
        // this an early "subscribed" result would be lost and the user would
        // fall back to the free-tier count.
        try {
            if (
                window.subscriptionChecker?.isSubscribed === true ||
                window.backendSubscriptionChecker?.isSubscribed === true ||
                window.userIsSubscribed === true
            ) {
                this._isSubscribed = true;
                console.log(
                    "[QuestionCounter] Seeded subscribed=true from live checker on init",
                );
            }
        } catch (_e) {
            /* defensive */
        }

        window.addEventListener('subscriptionStatusChecked', (event) => {
            const reported = event?.detail?.isSubscribed === true;
            // Never let one checker's "not subscribed" clobber a "subscribed"
            // confirmed by the other checker. Only lower the stored flag when
            // NO live checker still reports subscribed (e.g. genuine sign-out).
            if (reported) {
                this._isSubscribed = true;
            } else if (!this.isSubscribed) {
                this._isSubscribed = false;
            }
            console.log(`[QuestionCounter] Subscription status updated (reported=${reported}, effective=${this.isSubscribed})`);

            // If user is subscribed, they have unlimited questions
            if (this.isSubscribed) {
                console.log("[QuestionCounter] User is subscribed - unlimited questions available");
            }
        });
    }
    
    async initializeForUser(userId) {
        try {
            console.log(
                "[QuestionCounter] Initializing cache for user:",
                userId,
            );
            // De-duplicate via lock so UI can't double-run this
            await this._withLock(`initializeForUser:${userId}`, async () => {
                await this.getQuestionCount(userId);
            });
            console.log(
                "[QuestionCounter] Cache initialized with count:",
                this.cachedCount,
            );
        } catch (error) {
            console.error(
                "[QuestionCounter] Failed to initialize cache:",
                error,
            );
        }
    }

    async resetQuestionCount(userId, count = 10) {
        await this.ensureReady();
        if (!userId) throw new Error("User ID is required");

        const { doc, setDoc, serverTimestamp } = this.modules;
        const ref = doc(this.db, "userProfiles", userId);

        try {
            await setDoc(
                ref,
                {
                    Count: count,
                    resetAt: serverTimestamp?.() ?? new Date().toISOString(),
                },
                { merge: true },
            );

            this.cachedCount = count;
            console.log(
                `[QuestionCounter] Reset Count to ${count} for user ${userId}`,
            );
            return count;
        } catch (error) {
            console.error(
                "[QuestionCounter] Error resetting question count:",
                error,
            );
            throw error;
        }
    }
}

// Expose globally for production use
window.QuestionCounterManager = QuestionCounterManager;
